import {
    Box,
    Link,
    useTheme
} from '@mui/material';
import {
    type FC,
    type ReactElement,
    useMemo,
    useRef
} from 'react';
import {
    Link as RRLink
} from 'react-router-dom';
import Slider from 'react-slick';

import CategoryIcon from '../CategoryIcon';
import Typography from '../Typography';

import useSlickAriaHiddenFix from '@utils/useSlickAriaHiddenFix';

import type {
    CategoriesCarouselProps
} from './types';

import styles from './styles';

export const CATEGORY_ID_PLACEHOLDER = 'CATEGORY_ID_PLACEHOLDER' as const;

const CategoriesCarousel: FC<CategoriesCarouselProps> = (props) => {
    const {
        categoriesList,
        isSmallItems = false,
        linkStructure,
        settings
    } = props;

    const muiTheme = useTheme();

    const carouselRef = useRef<HTMLDivElement | null>(null);

    const sliderSettings = useSlickAriaHiddenFix(
        carouselRef,
        settings,
        {
            focusableSelector: 'a'
        }
    );

    const categoriesNodes = useMemo<ReactElement[]>(
        () => categoriesList.map((categoryDetails) => (
            <Box key={categoryDetails.id}>
                <Link
                    component={RRLink}
                    sx={[
                        styles.categoryContainer,
                        isSmallItems && styles.categoryContainerSmallItems
                    ]}
                    textAlign={'center'}
                    to={linkStructure.replace(CATEGORY_ID_PLACEHOLDER, String(categoryDetails.id))}
                >
                    <CategoryIcon
                        className={'icon'}
                        fill={muiTheme.colors.plantation}
                        icon={categoryDetails.icon}
                        stroke={muiTheme.colors.plantation}
                    />
                    <Typography
                        sx={{
                            mt: 1
                        }}
                        variant={'caption'}
                    >
                        {categoryDetails.name}
                    </Typography>
                    <Box sx={styles.underline} />
                </Link>
            </Box>
        )),
        [
            categoriesList,
            isSmallItems,
            linkStructure,
            muiTheme.colors.plantation
        ]
    );

    return (
        <Box
            aria-label={'Supporter categories'}
            aria-roledescription={'carousel'}
            ref={carouselRef}
            role={'group'}
            sx={[
                styles.carousel,
                isSmallItems && styles.carouselSmallItems
            ]}
        >
            <Slider {...sliderSettings}>
                {categoriesNodes}
            </Slider>
        </Box>
    );
};

export default CategoriesCarousel;
